import { useState, useEffect, useCallback, useRef } from 'react';
import { getSearch, searchV2, searchGames, getValidToken } from '../services/getSearch';
import { parseSearchGamesResponse } from '../functions/functions';
import { useInitialResults } from '../hooks/useInitialResults';
import Loading from './loading/Loading';
import SkeletonLoader from './skeleton/SkeletonLoader';
import Results from './results/results';
import ErrorService from './error/errorService';
import Unauthorized from './error/unauthorized';
import WithoutResults from './withoutResults/withoutResults';

export default function BuscadorApp() {
  const initialResults = useInitialResults();
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [dataGames, setDataGames] = useState(null);
  const [showLoading, setShowLoading] = useState(true);
  const [pathURL, setPathURL] = useState('');
  const [searchParamsChanged, setSearchParamsChanged] = useState(false);
  const [isDomainAllowed, setIsDomainAllowed] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 850);
  const [searchError, setSearchError] = useState(false);
  const [serviceError, setServiceError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [notesReady, setNotesReady] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const abortControllerRef = useRef(null);

  /**
   * Detecta si el dispositivo es móvil basado en el ancho de pantalla
   * 
   * @description Actualiza el estado isMobile evaluando si el ancho de la ventana
   * es menor o igual a 850px. Esta función es utilizada como callback en el
   * event listener de resize para adaptar la interfaz responsivamente.
   */
  const isMobileDevice = () => {
    setIsMobile(window.innerWidth <= 850);
  };

  /**
   * Configura la detección responsiva de dispositivos móviles
   * 
   * @description Registra un event listener para el evento 'resize' que actualiza
   * automáticamente el estado isMobile cuando cambia el tamaño de la ventana.
   * Se ejecuta solo al montar el componente y limpia el listener al desmontarlo.
   */
  useEffect(() => {
    window.addEventListener('resize', isMobileDevice);

    return () => {
      window.removeEventListener('resize', isMobileDevice);
    };
  }, []);

  /**
   * Valida que el dominio actual esté autorizado para usar el widget
   * 
   * @description Verifica si el hostname actual pertenece a los dominios permitidos:
   * clarosports.com, unotv.com, amxinfra o localhost. Actualiza el estado
   * isDomainAllowed que determina si se muestra el widget o el componente
   * Unauthorized. Se ejecuta solo al montar el componente.
   */
  useEffect(() => {
    const domain = window.location.hostname;
    const allowedDomains = ['clarosports.com', 'amxinfra', 'unotv.com', 'localhost'];
    const isAllowed = allowedDomains.some(d => domain.includes(d));
    setIsDomainAllowed(isAllowed);
  }, []);

  /**
 * Recibe parámetros y origen del iframe padre
 * 
 * @description Escucha mensajes del iframe padre para recibir los parámetros
 * de búsqueda (q, from, category) y el origen (dominio) del sitio padre.
 * También recibe búsquedas directas desde el input de WordPress.
 * Actualiza el estado con estos valores para sincronizar la búsqueda.
 */
  useEffect(() => {
    const handleMessage = (event) => {
      // Recibir parámetros de la URL
      if (event.data.params !== undefined) {
        const urlParams = new URLSearchParams(event.data.params);
        const q = urlParams.get('q');

        // Tomar search_session_id desde los params de WordPress
        const wpSessionId = urlParams.get('search_session_id');
        if (wpSessionId) {
          setSessionId(wpSessionId);
        }

        if (q) {
          setQuery(q);
          setSearchParamsChanged(true);
        } else {
          // Si no hay parámetro q, limpiar búsqueda
          console.log('Limpiando búsqueda - sin parámetro q');
          setQuery('');
          setData(null);
        }
      }

      // Recibir búsqueda directa desde el input de WordPress
      if (event.data.type === 'search' && event.data.query) {
        console.log('Búsqueda desde WordPress:', event.data.query);
        setQuery(event.data.query);
        setSearchParamsChanged(true);
      }

      // Recibir search_session_id directamente desde WordPress
      if (event.data.search_session_id) {
        setSessionId(event.data.search_session_id);
      }

      // Recibir señal de limpiar búsqueda desde WordPress
      if (event.data.type === 'clearSearch') {
        console.log('Limpiando búsqueda - señal explícita');
        setQuery('');
        setData(null);
      }

      if (event.data.origin) {
        setPathURL(event.data.origin);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);


  /**
   * Sincroniza el estado inicial con los parámetros de la URL
   * 
   * @description Lee los parámetros de búsqueda de la URL (q)
   * y actualiza los estados correspondientes solo si están vacíos. Esto permite
   * que el widget mantenga el estado de búsqueda al recargar la página o al
   * recibir parámetros del iframe padre. Se ejecuta solo al montar el componente.
   */
  useEffect(() => {
    if (!query) {
      const urlParams = new URLSearchParams(window.location.search);
      setQuery(urlParams.get('q') || '');
      setSearchParamsChanged(true);
    }
  }, []);

  /**
   * Detecta cambios en la URL y limpia resultados si no hay parámetro q
   * 
   * @description Monitorea cambios en la URL usando popstate (navegación hacia atrás/adelante)
   * y limpia los resultados si el parámetro q es removido o está vacío.
   */
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const q = urlParams.get('q');

      if (!q) {
        setQuery('');
        setData(null);
      } else {
        setQuery(q);
        setSearchParamsChanged(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  /**
   * Actualiza la altura del iframe cuando cambia el contenido
   * 
   * @description Cada vez que el data cambia (nuevos chunks llegan),
   * actualiza la altura del iframe padre para que se ajuste al contenido.
   * También notifica a WordPress que la búsqueda terminó.
   */
  useEffect(() => {
    if (data?.resume) {
      setTimeout(sendHeight, 50);

      // Notificar a WordPress que la búsqueda terminó
      if (pathURL) {
        const message = {
          type: 'searchComplete',
          hasResults: data?.hits?.length > 0 || data?.resume?.length > 0
        };
        window.parent.postMessage(message, pathURL);
      }
    }
  }, [data?.resume, pathURL]);

  /**
   * Ejecuta la búsqueda principal con manejo de estados y errores
   * 
   * @description Función principal que realiza dos búsquedas en paralelo:
   * una con source="cs_v2" (con streaming) y otra con source="generate_query".
   * Si generate_query es exitosa, ejecuta searchV2 con los datos.
   * Previene búsquedas duplicadas mientras una está en progreso.
   * Actualiza la URL del navegador y maneja diferentes tipos de respuesta.
   */
  const performSearch = useCallback(async () => {
    if (!query.trim()) {
      setShowLoading(false);
      return;
    }

    // Prevenir búsquedas duplicadas mientras una está en progreso
    if (isSearching) {
      return;
    }

    try {
      setShowLoading(true);
      setIsSearching(true);

      // Notificar a WordPress que la búsqueda comenzó
      if (pathURL) {
        const message = {
          type: 'searchStart',
          query: query
        };
        window.parent.postMessage(message, pathURL);
      }

      // Resetear datos al iniciar nueva búsqueda
      setData({ resume: '', hits: [], total: 0 });
      setNotesReady(false);

      // Cancelar búsqueda anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const url = new URL(window.location.href);
      url.searchParams.set('q', query);
      window.history.pushState({}, '', url);

      // Obtener token una sola vez al inicio
      const accessToken = await getValidToken();

      // Callback para procesar chunks de streaming de utv_v3
      const handleStreamChunk = (chunkContent) => {

        if (!chunkContent || typeof chunkContent !== 'string') {
          return;
        }

        // Activar indicador de streaming
        setIsStreaming(true);

        // Actualizar datos con el chunk recibido (texto plano)
        setData(prevData => {
          const currentData = prevData || { resume: '', hits: [], total: 0 };
          const newResume = currentData.resume + chunkContent;

          return {
            resume: newResume,
            hits: currentData.hits,
            total: currentData.total
          };
        });
      };

      // Función auxiliar para ejecutar búsqueda de juegos
      const executeGameSearch = () => {
        getSearch(query, 'search_games', null, accessToken, sessionId)
          .then(result => {
            const gamesData = parseSearchGamesResponse(result.data[0]);
            if (gamesData) {
              searchGames(gamesData)
                .then(gamesResult => {
                  if (gamesResult?.statusCode === 200) {
                    setDataGames(gamesResult.data);
                  } else {
                    console.warn('[performSearch] Error en searchGames:', gamesResult?.statusCode);
                  }
                })
                .catch(err => console.error('[performSearch] Error ejecutando searchGames:', err));
            }
          })
          .catch(err => console.error('[performSearch] Error en search_games:', err));
      };

      // Ejecutar búsqueda de juegos (fire-and-forget)
      executeGameSearch();

      // Promesa que se resuelve cuando llega el PRIMER chunk de cs_v3
      let resolveFirstChunk;
      const firstChunkPromise = new Promise(resolve => { resolveFirstChunk = resolve; });

      const originalHandleChunk = handleStreamChunk;
      let firstChunkReceived = false;
      const wrappedHandleChunk = (chunkContent) => {
        originalHandleChunk(chunkContent);
        if (!firstChunkReceived) {
          firstChunkReceived = true;
          resolveFirstChunk();
        }
      };

      // Lanzar streaming (fire-and-forget, sigue en background)
      getSearch(query, 'cs_v4', wrappedHandleChunk, accessToken, sessionId)
        .then(() => { setIsStreaming(false); })
        .catch(err => {
          console.error('[performSearch] Error en cs_v3:', err);
          setIsStreaming(false);
          resolveFirstChunk(); // resolver para no bloquear si falla
        });

      // Promesa de generate_query + searchV2 (notas)
      const notesPromise = (async () => {
        const search2 = await getSearch(query, 'generate_query_v2', null, accessToken, sessionId);

        if (search2?.statusCode === 200 && search2?.data) {
          const v2Result = await searchV2(search2.data);

          if (v2Result?.statusCode === 200) {
            setSearchError(false);
            setServiceError(null);

            const responseData = v2Result.data || {};
            const notes = responseData.hits != null && Array.isArray(responseData.hits) ? responseData.hits : [];

            setData(prevData => ({
              resume: prevData?.resume || '',
              hits: notes,
              total: responseData.total || notes.length
            }));
            setNotesReady(true);
            return;
          }
        }

        // Si generate_query falla, solo mostrar el resume del streaming
        setSearchError(false);
        setServiceError(null);
        setNotesReady(true);
      })();

      // Esperar a que llegue el primer chunk de streaming Y las notas terminen
      await Promise.all([firstChunkPromise, notesPromise]);

    } catch (error) {
      console.error('Error en búsqueda:', error);
      setSearchError(true);

      // Notificar a WordPress que hubo error
      if (pathURL) {
        const message = {
          type: 'searchError',
          error: error.message
        };
        window.parent.postMessage(message, pathURL);
      }
    } finally {
      setShowLoading(false);
      setIsSearching(false);

      // Notificar a WordPress que la búsqueda terminó
      if (pathURL) {
        const message = {
          type: 'searchEnd'
        };
        window.parent.postMessage(message, pathURL);
      }
    }
  }, [query, isSearching]);

  /**
   * Ejecuta la búsqueda cuando cambian los parámetros
   * 
   * @description Orquesta la ejecución de búsquedas cuando se detectan cambios
   * en los parámetros. Realiza la búsqueda, envía parámetros al iframe padre
   * y actualiza la altura del contenedor. Solo se ejecuta si no hay errores
   * de búsqueda activos.
   */
  useEffect(() => {
    if (searchParamsChanged && !searchError) {
      setSearchParamsChanged(false);
      performSearch();
      sendParams({ q: query });
      setTimeout(sendHeight, 500);
    }
  }, [searchParamsChanged, searchError, performSearch, query]);

  /**
   * Reinicia los datos para nuevas búsquedas en dispositivos móviles
   * 
   * @description Limpia los datos existentes cuando se inicia una nueva búsqueda
   * (query o categoría) en la primera página y en modo móvil. Esto evita que
   * se muestren resultados anteriores mientras se cargan los nuevos en el
   * patrón de scroll infinito móvil.
   */
  useEffect(() => {
    if (query) {
      setData(null);
    }
  }, [query])

  /**
   * Maneja el cambio del término de búsqueda desde WordPress
   * 
   * @description Actualiza el query de búsqueda recibido desde el input de WordPress.
   * Ignora cambios mientras una búsqueda está en progreso.
   */
  const handleQueryChange = (newQuery) => {
    // Prevenir cambios mientras se está buscando
    if (isSearching) {
      return;
    }

    // Validar tipo y sanitizar entrada
    if (typeof newQuery !== 'string') {
      console.warn('Query debe ser string');
      return;
    }

    // Sanitizar entrada
    const sanitizedQuery = newQuery.trim().slice(0, 500);

    // Validar caracteres peligrosos
    const dangerousChars = /<script|javascript:|data:/i;
    if (dangerousChars.test(sanitizedQuery)) {
      console.warn('Query contiene caracteres no permitidos');
      return;
    }

    setQuery(sanitizedQuery);
    setSearchParamsChanged(true);
  };

  /**
   * Reinicia el formulario de búsqueda y limpia todos los parámetros
   * 
   * @description Restablece todos los estados del buscador a sus valores iniciales.
   * También limpia los parámetros de búsqueda de la URL del navegador.
   */
  const handleReset = () => {
    setQuery('');
    setData(null);

    // Limpiar querystrings de la URL
    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    window.history.pushState({}, '', url);

    // Notificar a WordPress que se limpió la búsqueda
    if (pathURL) {
      const data = { type: 'reset' };
      window.parent.postMessage(data, pathURL);
    }
  };

  /**
   * Envía la altura del contenedor al iframe padre para ajuste automático
   * 
   * @description Calcula la altura actual del contenedor principal y la envía
   * al iframe padre mediante postMessage. Esto permite que el sitio padre ajuste
   * dinámicamente la altura del iframe según el contenido del widget.
   * Establece una altura mínima de 500px.
   * Intenta múltiples selectores para asegurar que encuentra el contenedor.
   */
  const sendHeight = () => {
    if (!pathURL) return;

    // Intentar obtener altura de múltiples selectores
    let container = document.querySelector('.container');
    if (!container) {
      container = document.querySelector('main');
    }
    if (!container) {
      container = document.body;
    }

    if (container) {
      const computedStyle = window.getComputedStyle(container);
      const marginTop = parseFloat(computedStyle.marginTop) || 0;
      const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

      let height = container.offsetHeight + marginTop + marginBottom;

      // Asegurar altura mínima de 500px
      height = Math.max(height, 500);

      const data = { type: 'resize', height };
      window.parent.postMessage(data, pathURL);

    } else {
      console.warn('[sendHeight] No se encontró contenedor');
    }
  };

  /**
   * Envía los parámetros de búsqueda al iframe padre para sincronización
   * 
   * @description Comunica los parámetros actuales de búsqueda (query, categoría, página)
   * al sitio padre mediante postMessage. Esto permite mantener sincronizada la URL
   * del sitio padre con el estado actual del widget de búsqueda.
   */
  const sendParams = (params) => {
    if (pathURL) {
      const data = { type: 'params', params };
      window.parent.postMessage(data, pathURL);
    }
  };

  /**
   * Configura listeners para actualizar la altura del iframe automáticamente
   * 
   * @description Registra event listeners para los eventos 'load' y 'resize' del window
   * que ejecutan sendHeight para mantener actualizada la altura del iframe padre.
   * Se ejecuta cuando pathURL cambia y limpia los listeners al desmontar el componente
   * para evitar memory leaks. También usa MutationObserver para detectar cambios en el DOM.
   */
  useEffect(() => {
    // Forzar altura mínima de 500px en el documento
    document.documentElement.style.minHeight = '500px';
    document.body.style.minHeight = '500px';

    // Enviar altura inicial después de que el componente esté montado
    const timeouts = [100, 300, 600].map(delay =>
      setTimeout(sendHeight, delay)
    );

    // Listeners para eventos de carga y redimensionamiento
    window.addEventListener('load', sendHeight);
    window.addEventListener('resize', sendHeight);

    // MutationObserver para detectar cambios en el DOM
    const observer = new MutationObserver(() => {
      sendHeight();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
      window.removeEventListener('load', sendHeight);
      window.removeEventListener('resize', sendHeight);
      observer.disconnect();
    };
  }, [pathURL]);

  /**
   * Validación de seguridad por dominio
   * 
   * @description Verifica si el dominio actual está autorizado para usar el widget.
   * Si el dominio no está en la lista de permitidos (clarosports.com, unotv.com,
   * localhost), muestra el componente Unauthorized y bloquea el acceso al buscador.
   */
  if (isDomainAllowed === false) {
    return (
      <Unauthorized />
    );
  }

  return (
    <main className="grid-layout">
      {showLoading && query &&
        <SkeletonLoader count={initialResults} />
      }

      {/* {showLoading && !query && (
        <Loading message="Escribe tu búsqueda para comenzar..." />
      )} */}

      {serviceError && <ErrorService />}

      {!showLoading && !serviceError && query
        ? <Results data={data} query={query} isMobile={isMobile} isStreaming={isStreaming} notesReady={notesReady} dataGames={dataGames} />
        : !showLoading && !serviceError && !query && <WithoutResults />
      }

    </main>
  );
};